export interface OfferPrice {
  amount: number;
  currency: string;
  type: string;
}

export interface TripSectionItem {
  title: string;
  body: string;
}

export interface ParsedTripSection {
  key: string;
  title: string;
  body?: string;
  items?: TripSectionItem[];
  bullets?: string[];
}

export interface ParsedTripContent {
  sections: Record<string, ParsedTripSection>;
  order: string[];
}

export interface TripPayload {
  durationDays?: number;
  groupMax?: number;
  status?: string;
  content: Record<string, ParsedTripContent>;
}

export interface Offer {
  id: string;
  name: string;
  slug?: string;
  description?: string;
  image?: string;
  medias: string[];
  price?: OfferPrice;
  trip?: TripPayload;
  /** Sortable ranking for the homepage spotlight cycler. Null = not featured. */
  featuredOrder?: number;
  /** Personas this trip belongs to (e.g. ["women","mixed"]) — drives which
   *  persona landing pages list it. Empty/absent = shown on none. */
  personas?: string[];
  /** True if this offer is sourced from a Trip record without a matching HL
   *  Product — no price/checkout, used for editorial / coming-soon trips. */
  recordOnly?: boolean;
}

/** Site content snapshot: slug → parsed Markdown per locale.
 *  Sourced from custom_objects.site records, baked at build time. */
export interface SiteSnapshot {
  /** Map of section slug → { en: parsed, es: parsed }. */
  [slug: string]: { en: ParsedTripContent; es: ParsedTripContent };
}

const API_BASE = import.meta.env.VITE_API_BASE ?? "http://localhost:8787";

export async function fetchOffers(): Promise<Offer[]> {
  const res = await fetch(`${API_BASE}/products`);
  if (!res.ok) throw new Error(`offers: HTTP ${res.status}`);
  const body = (await res.json()) as { products: Offer[] };
  return body.products ?? [];
}

export function formatPrice(p: OfferPrice | undefined, locale: "en" | "es"): string {
  if (!p) return "";
  try {
    return new Intl.NumberFormat(locale === "es" ? "es-ES" : "en-IE", {
      style: "currency",
      currency: p.currency,
      maximumFractionDigits: 0,
    }).format(p.amount);
  } catch {
    return `${p.amount} ${p.currency}`;
  }
}
